import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Popconfirm, Segmented, Skeleton } from 'antd'
import {
  ArrowLeftOutlined,
  CameraOutlined,
  ClockCircleOutlined,
  CloudSyncOutlined,
  DesktopOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  LoadingOutlined,
  PoweroffOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { Button, toast } from '@crosscode/gravity-components'
import { Page } from '@/shared/Page'
import { api } from '@/api/client'
import { useStore } from '@/app/StoreProvider'
import { useAuth } from '@/app/AuthProvider'
import { useFtpClient } from '@/features/ftp/FtpClientHost'
import { useRemoteDesktop } from '@/features/remote/RemoteDesktopHost'
import { CashTerminalLogsModal } from '@/features/cash/CashTerminalLogsModal'
import { CashCameraPickerModal } from '@/features/cash/CashCameraPickerModal'
import {
  CashCameraLive,
  type CashCameraClipRequest,
} from '@/features/cash/CashCameraLive'
import { CashPosFeed } from '@/features/cash/CashPosFeed'
import { CashOfdFeed } from '@/features/cash/CashOfdFeed'
import { fetchStoreIncidentsSummary } from '@/features/pos-incidents/api'
import { updateTerminal } from '@/features/cash/api'
import { fetchTerminalRemote } from '@/features/cash/terminalRemote'
import { buildPosArchiveClip } from '@/features/trassir/utils'
import type { SystemInfo, TerminalCameraBinding, TerminalDetails, TerminalMeta } from '@/features/cash/types'
import { isUkm5 } from '@/features/cash/utils'
import { useSystemInfoLive } from '@/features/cash/useSystemInfoLive'
import styles from './CashDetailPage.module.css'

function formatOsVersion(raw?: string) {
  if (!raw) return null
  const desc = raw.match(/Description:\s*(.+)/i)?.[1]
  if (desc) return desc.replace(/"/g, '').trim()
  const pretty = raw.match(/PRETTY_NAME="([^"]+)"/)?.[1]
  if (pretty) return pretty
  return raw.split('\n').find((l) => l.trim())?.trim() || raw
}

function shortShift(label: string) {
  if (/открыт/i.test(label)) return 'Открыта'
  if (/закрыт/i.test(label)) return 'Закрыта'
  return label
}

function shortFn(label: string) {
  if (label.includes('Всё в порядке')) return 'ОК'
  if (/нет данных/i.test(label)) return 'Нет данных'
  if (/ошибк/i.test(label)) return 'Ошибка'
  return label
}

function Metric({
  name,
  percent,
  detail,
  error,
}: {
  name: string
  percent?: number
  detail?: string
  error?: string
}) {
  if (error) {
    return (
      <div className={styles.metric}>
        <div className={styles.metricLabel}>
          <span className={styles.metricName}>{name}</span>
          <span className={`${styles.metricPct} ${styles.metricPctErr}`}>—</span>
        </div>
        <p className={styles.metricDetail}>нет данных</p>
        <div className={styles.meterBar}>
          <div className={`${styles.meterFill} ${styles.meterFillErr}`} style={{ width: '100%', opacity: 0.2 }} />
        </div>
      </div>
    )
  }

  const p = percent ?? 0
  const tone =
    p >= 90 ? 'Err' : p >= 75 ? 'Warn' : 'Ok'
  return (
    <div className={styles.metric}>
      <div className={styles.metricLabel}>
        <span className={styles.metricName}>{name}</span>
        <span className={`${styles.metricPct} ${styles[`metricPct${tone}`]}`}>
          {Number.isFinite(p) ? `${Math.round(p)}%` : '—'}
        </span>
      </div>
      <p className={styles.metricDetail}>{detail || '\u00a0'}</p>
      <div className={styles.meterBar}>
        <div
          className={`${styles.meterFill} ${styles[`meterFill${tone}`]}`}
          style={{ width: `${Math.min(100, Math.max(0, p))}%` }}
        />
      </div>
    </div>
  )
}

function DlRow({ label, value, mono, tone }: { label: string; value?: ReactNode; mono?: boolean; tone?: 'ok' | 'warn' | 'err' }) {
  if (value == null || value === '') return null
  const toneClass =
    tone === 'ok' ? styles.toneOk : tone === 'warn' ? styles.toneWarn : tone === 'err' ? styles.toneErr : ''
  return (
    <div className={styles.dlRow}>
      <span className={styles.dlKey}>{label}</span>
      <span className={`${styles.dlVal} ${mono ? styles.mono : ''} ${toneClass}`.trim()}>{value}</span>
    </div>
  )
}

export function CashDetailPage() {
  const { id } = useParams()
  const { storeId, selectedStore } = useStore()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const { openFtp } = useFtpClient()
  const { openRemote } = useRemoteDesktop()

  const [meta, setMeta] = useState<TerminalMeta | null>(null)
  const [data, setData] = useState<TerminalDetails | null>(null)
  const [sysInfo, setSysInfo] = useState<SystemInfo | null>(null)
  const [sysLoading, setSysLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logsOpen, setLogsOpen] = useState(false)
  const [ntpSource, setNtpSource] = useState<'ntp' | 'local'>('ntp')
  const [actionBusy, setActionBusy] = useState(false)
  const [cameraPickerOpen, setCameraPickerOpen] = useState(false)
  const [sidePanel, setSidePanel] = useState<'info' | 'pos' | 'ofd'>('info')
  const [clipRequest, setClipRequest] = useState<CashCameraClipRequest | null>(null)
  const [incidentNewCount, setIncidentNewCount] = useState<number | null>(null)

  const terminalId = id || ''
  const canEditCamera =
    hasPermission('feature.settings.edit') && hasPermission('feature.trassir.view')
  const canViewCamera = hasPermission('feature.trassir.view')

  const load = useCallback(async () => {
    if (!storeId || !terminalId) return
    setLoading(true)
    setError(null)
    try {
      const list = await api<TerminalMeta[] | { terminals?: TerminalMeta[] }>(
        `/api/stores/${storeId}/terminals`,
      )
      const terminals = Array.isArray(list) ? list : list.terminals || []
      const found = terminals.find((t) => String(t.id) === terminalId) || null
      setMeta(found)

      if (!found) {
        setData(null)
        navigate('/cash', { replace: true })
        return
      }

      if (found.status === 'offline') {
        setData(null)
        setSysInfo(null)
        setSysLoading(false)
        return
      }

      const detail = await api<TerminalDetails>(`/api/terminals/${terminalId}`)
      setData(detail)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [storeId, terminalId, navigate])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setMeta(null)
    setData(null)
    setError(null)
    setSysInfo(null)
    if (storeId) setLoading(true)
  }, [storeId])

  useEffect(() => {
    setSidePanel('info')
    setSysInfo(null)
  }, [terminalId])

  const systemLiveEnabled = !!terminalId && meta?.status === 'online'

  useEffect(() => {
    if (systemLiveEnabled) setSysLoading(true)
    else setSysLoading(false)
  }, [systemLiveEnabled, terminalId])

  useSystemInfoLive(terminalId, systemLiveEnabled, {
    onInfo: (info) => {
      setSysInfo(info)
      setSysLoading(false)
    },
    onError: () => {
      setSysLoading(false)
    },
  })

  const terminalType = meta?.type
  const ukm5 = isUkm5(terminalType)
  const ukm4 = terminalType === 'УКМ-4'
  const online = meta?.status === 'online'
  const offline = meta?.status === 'offline'
  const title = `Касса ${meta?.num ?? meta?.name ?? terminalId}`
  const isShiftOpen = /открыт/i.test(data?.kkt?.status || '')

  const timeDiff = data?.time?.difference || ''
  const timeWarn = /час/i.test(timeDiff) || (/минут/i.test(timeDiff) && !/^0\s+минут/.test(timeDiff))

  const fnStatus = data?.fn?.status || ''
  const fnErr = /ошибк/i.test(fnStatus) || /подходит/i.test(fnStatus)

  const hasCamera = !!(meta?.trassir_server_id && meta?.trassir_channel_guid)

  useEffect(() => {
    if (sidePanel === 'pos' && !(hasCamera && canViewCamera)) {
      setSidePanel('info')
    }
  }, [sidePanel, hasCamera, canViewCamera])

  useEffect(() => {
    if (!storeId || !meta?.id || !canViewCamera) {
      setIncidentNewCount(null)
      return
    }
    let cancelled = false
    void fetchStoreIncidentsSummary(storeId)
      .then((sum) => {
        if (cancelled) return
        const row = sum.by_terminal?.find((t) => Number(t.terminal_id) === Number(meta.id))
        setIncidentNewCount(row?.new ?? 0)
      })
      .catch(() => {
        if (!cancelled) setIncidentNewCount(null)
      })
    return () => {
      cancelled = true
    }
  }, [storeId, meta?.id, canViewCamera])

  const saveCameraBinding = async (binding: TerminalCameraBinding) => {
    if (!meta || !storeId) throw new Error('Касса не найдена')
    await updateTerminal(meta.id, {
      object_id: Number(storeId),
      num: String(meta.num ?? ''),
      ip: String(meta.ip || ''),
      kso: isUkm5(meta.type),
      comment: meta.comment || '',
      trassir_server_id: binding.trassir_server_id,
      trassir_channel_guid: binding.trassir_channel_guid,
      trassir_channel_name: binding.trassir_channel_name,
      trassir_pos_guid: binding.trassir_pos_guid ?? null,
      trassir_pos_name: binding.trassir_pos_name ?? null,
    })
    toast.success(
      binding.trassir_channel_guid ? 'Камера привязана' : 'Привязка камеры сброшена',
    )
    void load()
  }

  const openPosMoment = (eventTsUs: number, allEventTsUs: number[] = []) => {
    if (!meta?.trassir_server_id || !meta?.trassir_channel_guid) {
      toast.warning('Сначала привяжите камеру')
      return
    }
    if (!canViewCamera) {
      toast.warning('Нет доступа к TRASSIR')
      return
    }
    const clip = buildPosArchiveClip(eventTsUs, allEventTsUs)
    if (!clip) {
      toast.error('Не удалось построить интервал архива')
      return
    }
    setClipRequest({
      start: clip.start,
      stop: clip.stop,
      startUs: clip.startUs,
      stopUs: clip.stopUs,
      localAsUtc: true,
      key: Date.now(),
    })
    const durSec = Math.max(1, Math.round((clip.stopUs - clip.startUs) / 1_000_000))
    toast.info(`Архив · ${durSec} с`)
  }

  const runAction = async (label: string, path: string, body?: unknown) => {
    setActionBusy(true)
    try {
      await api(path, { method: 'POST', body })
      toast.success(label)
      void load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка')
    } finally {
      setActionBusy(false)
    }
  }

  const copyIp = () => {
    if (!meta?.ip) return
    void navigator.clipboard?.writeText(String(meta.ip))
    toast.success('IP скопирован')
  }

  const openRemoteKind = async (kind: 'SSH' | 'VNC') => {
    const conn = await fetchTerminalRemote(terminalId, kind)
    if (!conn?.connectionToken && !conn?.url) return
    openRemote({
      title,
      type: kind,
      tunnelUrl: conn.tunnelUrl,
      connectionToken: conn.connectionToken,
      url: conn.url,
    })
  }

  const notFound = !loading && !meta && !error
  const osLabel = formatOsVersion(sysInfo?.osVersion)
  const detailLoading = Boolean(online && loading && !data)

  return (
    <Page
      title={title}
      subtitle={
        [selectedStore?.name, terminalType].filter(Boolean).join(' · ') || undefined
      }
      actions={
        <div className={styles.heroActions}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cash')}>
            Назад
          </Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Обновить
          </Button>
        </div>
      }
      loading={loading && !meta && !error}
      error={error}
      empty={notFound}
      emptyText="Касса не найдена"
      emptyDescription="Вернитесь к списку касс и выберите терминал"
      onRetry={load}
    >
      {meta ? (
        <div className={styles.shell}>
          <div className={styles.statusLine}>
            <span className={`${styles.statusItem} ${online ? styles.toneOk : styles.toneErr}`}>
              <i className={`${styles.dot} ${online ? styles.dotOk : styles.dotErr}`} />
              <strong>{online ? 'Онлайн' : 'Оффлайн'}</strong>
            </span>
            {meta.ip ? (
              <button type="button" className={styles.heroIp} onClick={copyIp} title="Скопировать IP">
                {meta.ip}
              </button>
            ) : null}
            {data?.kkt?.status ? (
              <span
                className={`${styles.statusItem} ${isShiftOpen ? styles.toneOk : styles.toneWarn}`}
              >
                <i className={`${styles.dot} ${isShiftOpen ? styles.dotOk : styles.dotWarn}`} />
                Смена · <strong>{shortShift(data.kkt.status)}</strong>
              </span>
            ) : detailLoading ? (
              <span className={`${styles.statusItem} ${styles.statusLoading}`}>
                <LoadingOutlined spin />
                Загрузка…
              </span>
            ) : null}
            {fnStatus ? (
              <span className={`${styles.statusItem} ${fnErr ? styles.toneErr : styles.toneOk}`}>
                <i className={`${styles.dot} ${fnErr ? styles.dotErr : styles.dotOk}`} />
                ФН · <strong>{shortFn(fnStatus)}</strong>
              </span>
            ) : null}
            {timeDiff ? (
              <span className={`${styles.statusItem} ${timeWarn ? styles.toneWarn : ''}`}>
                <i className={`${styles.dot} ${timeWarn ? styles.dotWarn : styles.dotOk}`} />
                Время · <strong>Δ {timeDiff}</strong>
              </span>
            ) : null}
          </div>

          {offline ? (
            <div className={styles.offlineBanner}>
              Касса недоступна по сети. Диагностика и управление сервисом недоступны, пока терминал
              снова не выйдет в онлайн.
            </div>
          ) : null}

          <div className={styles.body}>
            <div className={styles.stack}>
              {meta && (data || hasCamera || canEditCamera || detailLoading) ? (
                <section className={styles.surface}>
                  <div className={styles.surfaceHead}>
                    <h2 className={styles.surfaceTitle}>Обзор</h2>
                    {detailLoading ? <span className={styles.surfaceHint}>загрузка…</span> : null}
                    <div className={styles.surfaceHeadActions}>
                      {!detailLoading ? (
                        <Segmented
                          size="small"
                          value={sidePanel}
                          onChange={(v) => setSidePanel(v as 'info' | 'pos' | 'ofd')}
                          options={[
                            { value: 'info', label: 'Информация' },
                            ...(hasCamera && canViewCamera
                              ? [{ value: 'pos' as const, label: 'ActivePOS' }]
                              : []),
                            { value: 'ofd', label: 'ОФД' },
                          ]}
                        />
                      ) : null}
                      {!detailLoading && canViewCamera && meta?.id ? (
                        <Link
                          to={`/cash/incidents?terminalId=${meta.id}${
                            incidentNewCount && incidentNewCount > 0 ? '&status=new' : ''
                          }`}
                          className={styles.incidentsLink}
                        >
                          Инциденты
                          {incidentNewCount != null && incidentNewCount > 0
                            ? ` · ${incidentNewCount}`
                            : ''}
                        </Link>
                      ) : null}
                      {!detailLoading && canEditCamera ? (
                        <Button
                          size="small"
                          icon={<CameraOutlined />}
                          onClick={() => setCameraPickerOpen(true)}
                        >
                          {hasCamera ? 'Изменить' : 'Камера'}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  {detailLoading ? (
                    <div className={styles.detailSkeleton}>
                      <div className={styles.dlGrid}>
                        <div className={styles.dlCol}>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div key={`sk-a-${i}`} className={styles.dlRow}>
                              <span className={styles.dlKey}>
                                <span className={styles.skelBar} style={{ width: `${56 + (i % 3) * 12}px` }} />
                              </span>
                              <span className={styles.dlVal}>
                                <span className={styles.skelBar} style={{ width: `${88 + (i % 4) * 18}px` }} />
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className={styles.dlCol}>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div key={`sk-b-${i}`} className={styles.dlRow}>
                              <span className={styles.dlKey}>
                                <span className={styles.skelBar} style={{ width: `${48 + (i % 3) * 14}px` }} />
                              </span>
                              <span className={styles.dlVal}>
                                <span className={styles.skelBar} style={{ width: `${72 + (i % 4) * 20}px` }} />
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : hasCamera ? (
                    <div className={styles.overviewWithCam}>
                      <div className={styles.overviewCamCol}>
                        <div className={styles.overviewCam}>
                          {canViewCamera && meta.trassir_server_id && meta.trassir_channel_guid ? (
                            <CashCameraLive
                              serverId={Number(meta.trassir_server_id)}
                              channelGuid={meta.trassir_channel_guid}
                              channelName={meta.trassir_channel_name}
                              clipRequest={clipRequest}
                              onClipEnded={() => setClipRequest(null)}
                            />
                          ) : (
                            <div className={styles.overviewCamDenied}>Нет доступа к TRASSIR</div>
                          )}
                        </div>
                      </div>
                      <div className={styles.overviewSide}>
                        {canViewCamera ? (
                          <div
                            className={styles.overviewSideBody}
                            hidden={sidePanel !== 'pos'}
                            aria-hidden={sidePanel !== 'pos'}
                          >
                            <CashPosFeed
                              terminalId={Number(meta.id)}
                              enabled={sidePanel === 'pos'}
                              onOpenArchiveMoment={openPosMoment}
                            />
                          </div>
                        ) : null}

                        <div
                          className={styles.overviewSideBody}
                          hidden={sidePanel !== 'ofd'}
                          aria-hidden={sidePanel !== 'ofd'}
                        >
                          <CashOfdFeed
                            fsNumber={data?.fn?.number}
                            serialNumber={data?.serialNumber}
                            enabled={sidePanel === 'ofd'}
                          />
                        </div>

                        {sidePanel === 'info' || (sidePanel === 'pos' && !canViewCamera) ? (
                          <div className={`${styles.dlCol} ${styles.overviewFields}`}>
                            {data ? (
                              <>
                                <DlRow label="С/н" value={data.serialNumber || '—'} mono />
                                <DlRow label="Адрес" value={data.address || '—'} />
                                <DlRow label="IP" value={meta.ip || '—'} mono />
                                <DlRow
                                  label="Смена"
                                  value={data.kkt?.status || '—'}
                                  tone={isShiftOpen ? 'ok' : 'warn'}
                                />
                                {data.kkt?.config ? (
                                  <DlRow label="Конфиг ККТ" value={data.kkt.config} />
                                ) : null}
                                {data.kkt?.version ? (
                                  <DlRow label="Версия ККТ" value={data.kkt.version} />
                                ) : null}
                                <DlRow label="ФН номер" value={data.fn?.number || '—'} mono />
                                <DlRow
                                  label="ФН срок"
                                  value={
                                    data.fn?.exp
                                      ? `${data.fn.exp}${data.fn.expSource === 'ofd' ? ' · ОФД' : ''}`
                                      : '—'
                                  }
                                />
                                <DlRow
                                  label="ФН статус"
                                  value={fnStatus || '—'}
                                  tone={fnErr ? 'err' : 'ok'}
                                />
                                <DlRow label="Время кассы" value={data.time?.remoteTime || '—'} />
                                <DlRow
                                  label="Разница"
                                  value={timeDiff || '—'}
                                  tone={timeWarn ? 'warn' : undefined}
                                />
                                {meta.trassir_pos_name || meta.trassir_pos_guid ? (
                                  <DlRow
                                    label="ActivePOS"
                                    value={meta.trassir_pos_name || meta.trassir_pos_guid || '—'}
                                    mono
                                  />
                                ) : null}
                                {(data.Bank || data.bank) && (
                                  <DlRow label="Банк" value={data.Bank || data.bank} />
                                )}
                                {data.IDpinpad ? (
                                  <DlRow label="Пинпад" value={data.IDpinpad} mono />
                                ) : null}
                                {data.terminal ? (
                                  <DlRow label="Терминал" value={data.terminal} />
                                ) : null}
                                {data.piot ? (
                                  <DlRow
                                    label="Статус ТС ПиОТ"
                                    value={data.piot.label || '—'}
                                    tone={
                                      data.piot.active === 'active'
                                        ? 'ok'
                                        : data.piot.active === 'failed'
                                          ? 'err'
                                          : 'warn'
                                    }
                                  />
                                ) : null}
                              </>
                            ) : (
                              <DlRow label="IP" value={meta.ip || '—'} mono />
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : data ? (
                    sidePanel === 'ofd' ? (
                      <CashOfdFeed
                        fsNumber={data.fn?.number}
                        serialNumber={data.serialNumber}
                        enabled
                      />
                    ) : (
                    <div className={styles.dlGrid}>
                      <div className={styles.dlCol}>
                        <DlRow label="С/н" value={data.serialNumber || '—'} mono />
                        <DlRow label="Адрес" value={data.address || '—'} />
                        <DlRow label="IP" value={meta.ip || '—'} mono />
                        <DlRow
                          label="Смена"
                          value={data.kkt?.status || '—'}
                          tone={isShiftOpen ? 'ok' : 'warn'}
                        />
                        {data.kkt?.config ? <DlRow label="Конфиг ККТ" value={data.kkt.config} /> : null}
                        {data.kkt?.version ? <DlRow label="Версия ККТ" value={data.kkt.version} /> : null}
                      </div>
                      <div className={styles.dlCol}>
                        <DlRow label="ФН номер" value={data.fn?.number || '—'} mono />
                        <DlRow
                          label="ФН срок"
                          value={
                            data.fn?.exp
                              ? `${data.fn.exp}${data.fn.expSource === 'ofd' ? ' · ОФД' : ''}`
                              : '—'
                          }
                        />
                        <DlRow
                          label="ФН статус"
                          value={fnStatus || '—'}
                          tone={fnErr ? 'err' : 'ok'}
                        />
                        <DlRow label="Время кассы" value={data.time?.remoteTime || '—'} />
                        <DlRow
                          label="Разница"
                          value={timeDiff || '—'}
                          tone={timeWarn ? 'warn' : undefined}
                        />
                        {(data.Bank || data.bank) && (
                          <DlRow label="Банк" value={data.Bank || data.bank} />
                        )}
                        {data.IDpinpad ? <DlRow label="Пинпад" value={data.IDpinpad} mono /> : null}
                        {data.terminal ? <DlRow label="Терминал" value={data.terminal} /> : null}
                        {data.piot ? (
                          <DlRow
                            label="Статус ТС ПиОТ"
                            value={data.piot.label || '—'}
                            tone={
                              data.piot.active === 'active'
                                ? 'ok'
                                : data.piot.active === 'failed'
                                  ? 'err'
                                  : 'warn'
                            }
                          />
                        ) : null}
                      </div>
                    </div>
                    )
                  ) : (
                    <div className={styles.overviewCamDenied}>
                      Камера не привязана. Нажмите «Камера», чтобы выбрать канал TRASSIR.
                    </div>
                  )}
                </section>
              ) : null}

              {(sysInfo || sysLoading) && online ? (
                <section className={styles.surface}>
                  <div className={styles.surfaceHead}>
                    <h2 className={styles.surfaceTitle}>Ресурсы</h2>
                    {sysLoading ? <span className={styles.surfaceHint}>обновление…</span> : null}
                  </div>
                  {sysLoading && !sysInfo ? (
                    <div className={styles.skeletonPad}>
                      <Skeleton active paragraph={{ rows: 2 }} />
                    </div>
                  ) : (
                    <>
                      <div className={styles.metrics}>
                        <Metric name="CPU" percent={sysInfo?.cpuUsage} />
                        <Metric
                          name="RAM"
                          percent={sysInfo?.ram?.usagePercent}
                          detail={
                            sysInfo?.ram?.used && sysInfo.ram.total
                              ? `${sysInfo.ram.used} / ${sysInfo.ram.total}`
                              : undefined
                          }
                          error={sysInfo?.ram?.error}
                        />
                        <Metric
                          name="Диск"
                          percent={sysInfo?.disk?.usagePercent}
                          detail={
                            sysInfo?.disk?.used && sysInfo.disk.total
                              ? `${sysInfo.disk.used} / ${sysInfo.disk.total}`
                              : undefined
                          }
                          error={sysInfo?.disk?.error}
                        />
                        <Metric
                          name="Swap"
                          percent={sysInfo?.swap?.usagePercent ?? 0}
                          detail={
                            sysInfo?.swap?.used && sysInfo.swap.total
                              ? `${sysInfo.swap.used} / ${sysInfo.swap.total}`
                              : undefined
                          }
                          error={sysInfo?.swap?.error}
                        />
                      </div>
                      {osLabel || (sysInfo?.cpu?.model && !sysInfo.cpu.error) ? (
                        <div className={styles.sysLine}>
                          {osLabel ? <strong>{osLabel}</strong> : null}
                          {osLabel && sysInfo?.cpu?.model ? ' · ' : null}
                          {sysInfo?.cpu?.model && !sysInfo.cpu.error
                            ? `${sysInfo.cpu.model}${sysInfo.cpu.cores ? `, ${sysInfo.cpu.cores} ядра` : ''}`
                            : null}
                        </div>
                      ) : null}
                    </>
                  )}
                </section>
              ) : null}
            </div>

            <aside className={styles.ops}>
              <div className={styles.opsGroup}>
                <span className={styles.opsLabel}>Диагностика</span>
                <div className={styles.opsStack}>
                  <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={() => setLogsOpen(true)}
                    disabled={offline}
                    block
                  >
                    Логи
                  </Button>
                  <div className={styles.opsRow}>
                    {ukm5 ? (
                      <Button
                        icon={<DesktopOutlined />}
                        onClick={() => void openRemoteKind('VNC')}
                        disabled={offline}
                        block
                      >
                        VNC
                      </Button>
                    ) : null}
                    {ukm4 || ukm5 ? (
                      <Button
                        icon={<DesktopOutlined />}
                        onClick={() => void openRemoteKind('SSH')}
                        disabled={offline}
                        block
                      >
                        SSH
                      </Button>
                    ) : null}
                  </div>
                  {(ukm4 || ukm5) && meta.ip ? (
                    <Button
                      icon={<FolderOpenOutlined />}
                      onClick={() =>
                        openFtp({
                          connectionType: ukm5 ? 'ukm5' : 'ukm4',
                          host: String(meta.ip),
                          title: `FTP · ${title}`,
                        })
                      }
                      block
                    >
                      FTP
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className={styles.opsGroup}>
                <span className={styles.opsLabel}>Сервис</span>
                <div className={styles.opsStack}>
                  {hasPermission('feature.cash.actions.restart_service') ? (
                    <Popconfirm
                      title="Перезапустить сервис УКМ?"
                      onConfirm={() =>
                        void runAction(
                          'Сервис перезапущен',
                          `/api/terminals/${terminalId}/restart-service`,
                        )
                      }
                      disabled={offline}
                    >
                      <Button icon={<ReloadOutlined />} loading={actionBusy} disabled={offline} block>
                        {ukm5 ? 'Рестарт УКМ-5' : 'Рестарт УКМ-4'}
                      </Button>
                    </Popconfirm>
                  ) : null}
                </div>
              </div>

              {hasPermission('feature.cash.actions.sync_time') ? (
                <div className={styles.opsGroup}>
                  <span className={styles.opsLabel}>Время</span>
                  <div className={styles.syncBlock}>
                    <Segmented
                      size="small"
                      block
                      value={ntpSource}
                      onChange={(v) => setNtpSource(v as 'ntp' | 'local')}
                      options={[
                        { label: 'NTP', value: 'ntp' },
                        { label: 'Локал.', value: 'local' },
                      ]}
                      disabled={offline || isShiftOpen}
                    />
                    <Button
                      icon={<CloudSyncOutlined />}
                      disabled={offline || isShiftOpen}
                      loading={actionBusy}
                      onClick={() =>
                        void runAction(
                          'Синхронизация запущена',
                          `/api/terminals/${terminalId}/sync-time`,
                          { source: ntpSource },
                        )
                      }
                      block
                    >
                      Синхронизировать
                    </Button>
                    {isShiftOpen ? (
                      <p className={styles.syncHint}>
                        <ClockCircleOutlined /> Смена открыта — синхронизация недоступна
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {hasPermission('feature.cash.actions.reboot') ||
              hasPermission('feature.cash.actions.shutdown') ? (
                <div className={`${styles.opsGroup} ${styles.opsDanger}`}>
                  <span className={styles.opsLabel}>Питание</span>
                  <div className={styles.opsRow}>
                    {hasPermission('feature.cash.actions.reboot') ? (
                      <Popconfirm
                        title="Перезагрузить кассу?"
                        onConfirm={() =>
                          void runAction(
                            'Перезагрузка отправлена',
                            `/api/terminals/${terminalId}/reboot`,
                          )
                        }
                        disabled={offline}
                      >
                        <Button danger icon={<ReloadOutlined />} loading={actionBusy} disabled={offline} block>
                          Ребут
                        </Button>
                      </Popconfirm>
                    ) : null}
                    {hasPermission('feature.cash.actions.shutdown') ? (
                      <Popconfirm
                        title="Выключить кассу?"
                        onConfirm={() =>
                          void runAction(
                            'Команда выключения отправлена',
                            `/api/terminals/${terminalId}/shutdown`,
                          )
                        }
                        disabled={offline}
                      >
                        <Button danger icon={<PoweroffOutlined />} loading={actionBusy} disabled={offline} block>
                          Выкл.
                        </Button>
                      </Popconfirm>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </aside>
          </div>
        </div>
      ) : null}

      <CashTerminalLogsModal
        open={logsOpen}
        terminalId={terminalId}
        title={title}
        onClose={() => setLogsOpen(false)}
      />

      {storeId && meta ? (
        <CashCameraPickerModal
          open={cameraPickerOpen}
          storeId={Number(storeId)}
          terminalLabel={meta.num}
          initial={{
            trassir_server_id: meta.trassir_server_id ?? null,
            trassir_channel_guid: meta.trassir_channel_guid ?? null,
            trassir_channel_name: meta.trassir_channel_name ?? null,
            trassir_pos_guid: meta.trassir_pos_guid ?? null,
            trassir_pos_name: meta.trassir_pos_name ?? null,
          }}
          onClose={() => setCameraPickerOpen(false)}
          onSave={saveCameraBinding}
        />
      ) : null}
    </Page>
  )
}
